import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FilterDropdown from '../../components/FilterDropdown';
import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages } from '../../contexts/constants';
import { formatLocalDateTime, formatYearMonthByLocale } from '../../utils/utils';

// ─────────────────────────────────────────────
// 定数・型
// ─────────────────────────────────────────────

const REQUESTER_TYPES = [
  { flowId: "31", labelKey: "materialOfficer", requiresOrg: false },
  { flowId: "40", labelKey: "salesGeneralManager", requiresOrg: false },
  { flowId: "42", labelKey: "businessGeneralManager", requiresOrg: true },
] as const;

const getRequesterLabelKey = (flowId: string) =>
  REQUESTER_TYPES.find(r => r.flowId === flowId)?.labelKey ?? flowId;

const requiresOrgSelection = (flowId: string) =>
  REQUESTER_TYPES.find(r => r.flowId === flowId)?.requiresOrg ?? false;

type ApprovalRequesterRecord = {
  targetFlowId: string;
  organizationId: string;
  organizationName: string;
  fiscalPeriod: string;
  employeeId: string;
  employeeName: string;
  employeeOrganizationName: string;
  positionName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type HqOrg = {
  organizationId: string;
  organizationName: string;
  parentOrganizationId: string | null;
};

type Employee = {
  userId: string;
  userName: string;
  organizationId: string;
  organizationName: string;
  positionId: string;
  positionName: string;
};

type EditState = {
  isNew: boolean;
  targetFlowId: string;
  organizationId: string;
  fiscalPeriod: string;
  employeeId: string;
  employeeName: string;
  employeeOrganizationName: string;
  positionName: string;
  isActive: boolean;
  originalEmployeeId: string;
  originalFiscalPeriod: string;
};

const EMPTY_EDIT: EditState = {
  isNew: true,
  targetFlowId: "31",
  organizationId: "ALL",
  fiscalPeriod: "",
  employeeId: "",
  employeeName: "",
  employeeOrganizationName: "",
  positionName: "",
  isActive: true,
  originalEmployeeId: "",
  originalFiscalPeriod: "",
};

type FieldErrors = { [key: string]: string };

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────

const ApprovalRequesterMaster: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  const TITLE = t('features.MasterMaintenance.ApprovalRequesterMaster.title');

  // ─── スタイル（AccountingOrganizationと統一） ───
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "normal",
    wordBreak: "break-word" as const,
    backgroundColor: "gray.100",
  };
  const bodyCellStyle = {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  };
  const fixedColCellStyle = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
    boxShadow: "1px 0 0 #ccc",
  };
  const hoverStyle = { bg: "gray.200" };
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 200,
  };

  // ─── state ───
  const [masterData, setMasterData] = useState<ApprovalRequesterRecord[]>([]);
  const [selectedFiscalPeriod, setSelectedFiscalPeriod] = useState("");
  const [hqOrgs, setHqOrgs] = useState<HqOrg[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [edit, setEdit] = useState<EditState>(EMPTY_EDIT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [empSearch, setEmpSearch] = useState("");

  // タブ（依頼者種別）
  const [activeTab, setActiveTab] = useState<string>(REQUESTER_TYPES[0].flowId);

  // インラインフィルター
  const [filterOrgName, setFilterOrgName] = useState("");
  const [filterEmployeeName, setFilterEmployeeName] = useState("");
  const [filterEmpOrg, setFilterEmpOrg] = useState("");
  const [filterActive, setFilterActive] = useState<"" | boolean>("");

  const { isOpen: isOpenEdit, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [conflictPerson, setConflictPerson] = useState("");
  const cancelRef = useRef<HTMLButtonElement>(null);

  // ─── 初期データ取得 ───
  useEffect(() => {
    fetchMasterData();
    fetchEmployees();
    fetchHqOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await WebAPI.request({
        query: `query { getUserAll {
          userId userName organizationId organizationName positionId positionName
        }}`,
      });
      if (res?.data?.getUserAll) {
        // 複数所属の重複を除去（userId ごとに1件）
        const seen = new Set<string>();
        const unique: Employee[] = [];
        for (const u of res.data.getUserAll) {
          if (!seen.has(u.userId)) {
            seen.add(u.userId);
            unique.push(u);
          }
        }
        setEmployees(unique);
      }
    } catch (err) {
      console.error('fetchEmployees error:', err);
    }
  };

  const fetchHqOrgs = async () => {
    try {
      const res = await WebAPI.request({
        query: `query { getOrganizationWithHierarchy {
          organizationId organizationName parentOrganizationId
        }}`,
      });
      if (res?.data?.getOrganizationWithHierarchy) {
        // minebeamitsumi の直下 = 事業本部レベル（putApprover._getHeadquartersOrgId と同じ判定）
        const hq: HqOrg[] = res.data.getOrganizationWithHierarchy.filter(
          (o: HqOrg) => o.parentOrganizationId === "minebeamitsumi"
        );
        setHqOrgs(hq);
      }
    } catch (err) {
      console.error('fetchHqOrgs error:', err);
    }
  };

  // ─── 現在の会計年度（4月始まり） ───
  const getCurrentFiscalYear = useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    return m >= 4 ? y : y - 1;
  }, []);

  const isPastFiscalYear = (fp: string) => {
    if (!fp) return false;
    return parseInt(fp.split('-')[0], 10) < getCurrentFiscalYear();
  };

  // ─── フィルター済みデータ（アクティブタブ + インラインフィルター） ───
  const filteredData = masterData.filter(r => {
    if (r.targetFlowId !== activeTab) return false;
    if (filterOrgName && r.organizationName !== filterOrgName) return false;
    if (filterEmployeeName && !r.employeeName.toLowerCase().includes(filterEmployeeName.toLowerCase())) return false;
    if (filterEmpOrg && !r.employeeOrganizationName.toLowerCase().includes(filterEmpOrg.toLowerCase())) return false;
    if (filterActive !== "" && r.isActive !== filterActive) return false;
    return true;
  });

  // ─── 従業員候補（クライアント側検索） ───
  const filteredEmployees = employees.filter(e => {
    if (!empSearch) return true;
    const q = empSearch.toLowerCase();
    return (
      e.userName.toLowerCase().includes(q) ||
      e.organizationName.toLowerCase().includes(q)
    );
  });

  // ─── マスタデータ取得（fiscalPeriod 省略で全件、指定で絞り込み） ───
  const fetchMasterData = async (fiscalPeriod?: string) => {
    try {
      const res = await WebAPI.request({
        query: `query getApprovalRequesterList($fiscalPeriod: String) {
          getApprovalRequesterList(fiscalPeriod: $fiscalPeriod) {
            targetFlowId organizationId organizationName fiscalPeriod
            employeeId employeeName employeeOrganizationName positionName
            isActive createdAt updatedAt
          }
        }`,
        variables: { fiscalPeriod: fiscalPeriod ?? null },
      });
      if (res?.data?.getApprovalRequesterList) {
        setMasterData(res.data.getApprovalRequesterList);
      } else {
        console.error('getApprovalRequesterList failed:', res?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: t('features.MasterMaintenance.ApprovalRequesterMaster.entityLabel') }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('fetchMasterData error:', err);
      toast({
        title: TITLE,
        description: t('common.message.fetchListFailed', { name: t('features.MasterMaintenance.ApprovalRequesterMaster.entityLabel') }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // ─── 検索 ───
  const handleSearch = async () => {
    await fetchMasterData(selectedFiscalPeriod || undefined);
  };

  // ─── 検索クリア ───
  const handleClear = async () => {
    setSelectedFiscalPeriod("");
    await fetchMasterData();
  };

  // ─── 新規追加 ───
  const handleNewRecord = () => {
    setEdit({
      ...EMPTY_EDIT,
      targetFlowId: activeTab,
      organizationId: requiresOrgSelection(activeTab) ? "" : "ALL",
      fiscalPeriod: selectedFiscalPeriod,
    });
    setErrors({});
    setEmpSearch("");
    onOpenEdit();
  };

  // ─── 行クリック（編集） ───
  const handleRowClick = (record: ApprovalRequesterRecord) => {
    setEdit({
      isNew: false,
      targetFlowId: record.targetFlowId,
      organizationId: record.organizationId,
      fiscalPeriod: record.fiscalPeriod,
      employeeId: record.employeeId,
      employeeName: record.employeeName,
      employeeOrganizationName: record.employeeOrganizationName,
      positionName: record.positionName,
      isActive: record.isActive,
      originalEmployeeId: record.employeeId,
      originalFiscalPeriod: record.fiscalPeriod,
    });
    setErrors({});
    setEmpSearch("");
    onOpenEdit();
  };

  const handleFlowIdChange = (flowId: string) => {
    setEdit(prev => ({
      ...prev,
      targetFlowId: flowId,
      organizationId: requiresOrgSelection(flowId) ? "" : "ALL",
    }));
  };

  const handleSelectEmployee = (emp: Employee) => {
    setEdit(prev => ({
      ...prev,
      employeeId: emp.userId,
      employeeName: emp.userName,
      employeeOrganizationName: emp.organizationName,
      positionName: emp.positionName,
    }));
    setEmpSearch("");
  };

  // ─── バリデーション ───
  const validate = (): boolean => {
    const e: FieldErrors = {};
    if (!edit.fiscalPeriod) {
      e.fiscalPeriod = t('features.MasterMaintenance.ApprovalRequesterMaster.error.fiscalPeriodRequired');
    }
    if (!edit.employeeId) {
      e.employeeId = t('features.MasterMaintenance.ApprovalRequesterMaster.error.employeeRequired');
    }
    if (requiresOrgSelection(edit.targetFlowId) && !edit.organizationId) {
      e.organizationId = t('features.MasterMaintenance.ApprovalRequesterMaster.error.organizationRequired');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─── 保存（実処理） ───
  const executeSave = async () => {
    setIsSaving(true);
    try {
      const input = {
        targetFlowId: edit.targetFlowId,
        organizationId: edit.organizationId,
        fiscalPeriod: edit.fiscalPeriod,
        employeeId: edit.employeeId,
        isActive: edit.isActive,
        originalEmployeeId: edit.isNew ? null : edit.originalEmployeeId,
        originalFiscalPeriod: edit.isNew ? null : edit.originalFiscalPeriod,
      };

      const res = await WebAPI.request({
        query: `mutation putApprovalRequesterMaster($input: ApprovalRequesterMasterInput!) {
          putApprovalRequesterMaster(input: $input) { status }
        }`,
        variables: { input },
      });

      if (res?.data?.putApprovalRequesterMaster?.status === 'success') {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.ApprovalRequesterMaster.message.saved'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onCloseEdit();
        await fetchMasterData(selectedFiscalPeriod || undefined);
      } else if (res?.data?.putApprovalRequesterMaster?.status === 'expired') {
        toast({ title: TITLE, description: Messages.expiredMessage, status: 'warning', duration: 5000, isClosable: true });
      } else {
        toast({ title: TITLE, description: t('common.message.saveFailed'), status: 'error', duration: 5000, isClosable: true });
      }
    } catch (err) {
      console.error('handleSave error:', err);
      toast({ title: TITLE, description: t('common.message.saveFailed'), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsSaving(false);
    }
  };

  // ─── 保存ボタン押下：競合チェック → 確認ダイアログ or 即保存 ───
  const handleSave = async () => {
    if (isSaving) return;
    if (!validate()) return;

    // 有効で保存する場合のみ、同一スロットのアクティブ者を確認
    if (edit.isActive) {
      const conflict = masterData.find(r =>
        r.targetFlowId === edit.targetFlowId &&
        r.organizationId === edit.organizationId &&
        r.fiscalPeriod === edit.fiscalPeriod &&
        r.employeeId !== edit.originalEmployeeId &&
        r.isActive === true
      );
      if (conflict) {
        setConflictPerson(conflict.employeeName);
        setIsConfirmOpen(true);
        return;
      }
    }

    await executeSave();
  };

  // ─── 競合確認ダイアログ OK ───
  const handleConfirmOk = async () => {
    setIsConfirmOpen(false);
    await executeSave();
  };

  // ─── 無効化 ───
  const handleDeactivate = async () => {
    if (edit.isNew) return;
    try {
      const input = {
        targetFlowId: edit.targetFlowId,
        organizationId: edit.organizationId,
        fiscalPeriod: edit.fiscalPeriod,
        employeeId: edit.employeeId,
        isActive: false,
        originalEmployeeId: edit.originalEmployeeId,
      };

      const res = await WebAPI.request({
        query: `mutation putApprovalRequesterMaster($input: ApprovalRequesterMasterInput!) {
          putApprovalRequesterMaster(input: $input) { status }
        }`,
        variables: { input },
      });

      if (res?.data?.putApprovalRequesterMaster?.status === 'success') {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.ApprovalRequesterMaster.message.deleted'),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        onCloseEdit();
        await fetchMasterData(selectedFiscalPeriod || undefined);
      } else {
        toast({ title: TITLE, description: t('common.message.saveFailed'), status: 'error', duration: 5000, isClosable: true });
      }
    } catch (err) {
      console.error('handleDeactivate error:', err);
      toast({ title: TITLE, description: t('common.message.saveFailed'), status: 'error', duration: 5000, isClosable: true });
    }
  };

  // ─────────────────────────────────────────────
  // JSX
  // ─────────────────────────────────────────────
  return (
    <Box h="100%" mt="16px">

      {/* 上部操作バー */}
      <Flex gap={4} align="center" mb={3}>
        <HStack spacing={1}>
          <Text fontWeight="bold" whiteSpace="nowrap">
            {t('features.MasterMaintenance.ApprovalRequesterMaster.label.fiscalPeriodColon')}
          </Text>
          <Input
            type="month"
            value={selectedFiscalPeriod}
            onChange={e => setSelectedFiscalPeriod(e.target.value)}
            width="180px"
          />
          <Button colorScheme="blue" onClick={handleSearch} ml={2}>
            {t('common.button.search')}
          </Button>
          {selectedFiscalPeriod && (
            <Button variant="ghost" onClick={handleClear}>
              {t('common.button.clear')}
            </Button>
          )}
        </HStack>
        <Button colorScheme="blue" onClick={handleNewRecord}>
          {t('common.button.newRegistration')}
        </Button>
      </Flex>

      {/* 依頼者種別タブ */}
      <Tabs
        index={REQUESTER_TYPES.findIndex(r => r.flowId === activeTab)}
        onChange={i => { setActiveTab(REQUESTER_TYPES[i].flowId); setFilterOrgName(""); }}
        mb={2}
      >
        <TabList>
          {REQUESTER_TYPES.map(r => (
            <Tab key={r.flowId} fontWeight="bold">
              {t(`features.MasterMaintenance.ApprovalRequesterMaster.requesterTypeLabel.${r.labelKey}`)}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {REQUESTER_TYPES.map(r => (
            <TabPanel key={r.flowId} p={0} pt={2}>
              <TableContainer
                overflowX="auto"
                overflowY="auto"
                w="1400px"
                h="calc(100dvh - 220px)"
              >
                <Table
                  variant="simple"
                  style={{ tableLayout: "fixed", borderCollapse: "separate" }}
                  cellSpacing={0}
                  cellPadding={0}
                  className="mm-ceas-table"
                >
                  <colgroup>
                    <col width={160} />
                    <col width={200} />
                    <col width={180} />
                    <col width={200} />
                    <col width={140} />
                    <col width={100} />
                    <col width={200} />
                    <col width={200} />
                  </colgroup>
                  <Thead position="sticky" top={0} zIndex={2} boxShadow="0 -1px 0 #EEEEEE">
                    <Tr h={50}>
                      <Th {...headerCellStyle}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.field.fiscalPeriod')}
                      </Th>
                      <Th {...headerCellStyle}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.field.organization')}
                        <FilterDropdown
                          filterValue={filterOrgName}
                          setFilterValue={setFilterOrgName}
                          dataSource={hqOrgs}
                          propName="organizationName"
                        />
                      </Th>
                      <Th {...headerCellStyle}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.field.employeeName')}
                        <FilterTextBox
                          placeholder={t('common.placeholder.partialMatch')}
                          filterValue={filterEmployeeName}
                          setFilterValue={setFilterEmployeeName}
                        />
                      </Th>
                      <Th {...headerCellStyle}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.field.employeeOrganization')}
                        <FilterTextBox
                          placeholder={t('common.placeholder.partialMatch')}
                          filterValue={filterEmpOrg}
                          setFilterValue={setFilterEmpOrg}
                        />
                      </Th>
                      <Th {...headerCellStyle}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.field.position')}
                      </Th>
                      <Th {...headerCellStyle}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.field.isActive')}
                        <FilterDropdown
                          filterValue={filterActive}
                          setFilterValue={setFilterActive}
                          dataSource={[{ isActive: true }, { isActive: false }]}
                          propName="isActive"
                          displayNameFunc={(v: boolean) =>
                            v
                              ? t('features.MasterMaintenance.ApprovalRequesterMaster.label.active')
                              : t('features.MasterMaintenance.ApprovalRequesterMaster.label.inactive')
                          }
                        />
                      </Th>
                      <Th {...headerCellStyle}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.field.createdAt')}
                      </Th>
                      <Th {...headerCellStyle}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.field.updatedAt')}
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredData.length === 0 ? (
                      <Tr>
                        <Td colSpan={8} textAlign="center" py={8} color="gray.400">
                          {t('common.label.noData')}
                        </Td>
                      </Tr>
                    ) : (
                      filteredData.map((record) => {
                        const bgColor = record.isActive ? "white" : "gray.100";
                        return (
                          <Tr
                            key={`${record.targetFlowId}_${record.organizationId}_${record.fiscalPeriod}_${record.employeeId}`}
                            role="group"
                            _hover={{ bg: "gray.200" }}
                          >
                            <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>
                              <Button
                                variant="link"
                                textDecoration="underline"
                                color="blue.500"
                                onClick={() => handleRowClick(record)}
                                sx={{
                                  display: 'inline-block',
                                  maxW: '100%',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {formatYearMonthByLocale(record.fiscalPeriod)}
                              </Button>
                            </Td>
                            <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{record.organizationName}</Td>
                            <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{record.employeeName}</Td>
                            <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{record.employeeOrganizationName}</Td>
                            <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{record.positionName}</Td>
                            <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">
                              {record.isActive ? (
                                <Badge colorScheme="green">
                                  {t('features.MasterMaintenance.ApprovalRequesterMaster.label.active')}
                                </Badge>
                              ) : (
                                <Badge colorScheme="gray">
                                  {t('features.MasterMaintenance.ApprovalRequesterMaster.label.inactive')}
                                </Badge>
                              )}
                            </Td>
                            <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.createdAt)}</Td>
                            <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.updatedAt)}</Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      {/* 編集/新規モーダル */}
      <Modal
        isOpen={isOpenEdit}
        onClose={onCloseEdit}
        closeOnOverlayClick={false}
        scrollBehavior="inside"
        size="2xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {edit.isNew
              ? t('features.MasterMaintenance.ApprovalRequesterMaster.modal.newTitle')
              : t('features.MasterMaintenance.ApprovalRequesterMaster.modal.editTitle')}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={5} align="stretch">

              {/* 依頼者種別 */}
              <FormControl>
                <Flex align="center">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.ApprovalRequesterMaster.field.requesterType')}
                  </FormLabel>
                  <Select
                    value={edit.targetFlowId}
                    onChange={e => handleFlowIdChange(e.target.value)}
                    width={320}
                  >
                    {REQUESTER_TYPES.map(r => (
                      <option key={r.flowId} value={r.flowId}>
                        {t(`features.MasterMaintenance.ApprovalRequesterMaster.requesterTypeLabel.${r.labelKey}`)}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </FormControl>

              {/* 年度（type="month" ピッカー） */}
              <FormControl isInvalid={!!errors.fiscalPeriod}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.ApprovalRequesterMaster.field.fiscalPeriod')}
                    <Text as="span" color="red.500" ml={1}>*</Text>
                  </FormLabel>
                  <VStack align="start" spacing={0}>
                    <Input
                      type="month"
                      value={edit.fiscalPeriod}
                      onChange={e => setEdit(prev => ({ ...prev, fiscalPeriod: e.target.value }))}
                      width={240}
                      isInvalid={!!errors.fiscalPeriod}
                    />
                    {isPastFiscalYear(edit.fiscalPeriod) && (
                      <Text fontSize="xs" color="orange.500" mt={1}>
                        {t('features.MasterMaintenance.ApprovalRequesterMaster.message.pastYearWarning')}
                      </Text>
                    )}
                    <FormErrorMessage mt={0}>{errors.fiscalPeriod}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              {/* 組織（事業本部長確認依頼者のみ） */}
              {requiresOrgSelection(edit.targetFlowId) && (
                <FormControl isInvalid={!!errors.organizationId}>
                  <Flex align="start">
                    <FormLabel style={labelStyle}>
                      {t('features.MasterMaintenance.ApprovalRequesterMaster.field.organization')}
                      <Text as="span" color="red.500" ml={1}>*</Text>
                    </FormLabel>
                    <VStack align="start" spacing={0}>
                      <Select
                        value={edit.organizationId}
                        onChange={e => setEdit(prev => ({ ...prev, organizationId: e.target.value }))}
                        placeholder={t('common.placeholder.selectLabel')}
                        width={320}
                        isInvalid={!!errors.organizationId}
                      >
                        {hqOrgs.map(org => (
                          <option key={org.organizationId} value={org.organizationId}>
                            {org.organizationName}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage mt={0}>{errors.organizationId}</FormErrorMessage>
                    </VStack>
                  </Flex>
                </FormControl>
              )}

              <Divider />

              {/* 担当者選択 */}
              <FormControl isInvalid={!!errors.employeeId}>
                <FormLabel fontWeight="bold">
                  {t('features.MasterMaintenance.ApprovalRequesterMaster.field.employeeSelect')}
                  <Text as="span" color="red.500" ml={1}>*</Text>
                </FormLabel>

                {/* 選択済み表示 */}
                {edit.employeeId && (
                  <Box mb={2} p={2} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                    <Text fontSize="sm" fontWeight="bold" color="blue.700">
                      {t('features.MasterMaintenance.ApprovalRequesterMaster.label.selectedEmployee')}:
                      &nbsp;{edit.employeeName}
                      （{edit.employeeOrganizationName} / {edit.positionName}）
                    </Text>
                  </Box>
                )}

                {/* 検索フィールド */}
                <InputGroup mb={2}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    value={empSearch}
                    onChange={e => setEmpSearch(e.target.value)}
                    placeholder={t('features.MasterMaintenance.ApprovalRequesterMaster.placeholder.empSearch')}
                  />
                </InputGroup>

                {/* 候補テーブル */}
                <TableContainer
                  maxH="200px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <Table size="sm" variant="simple" style={{ tableLayout: "fixed" }} cellSpacing={0} cellPadding={0}>
                    <colgroup>
                      <col width={180} />
                      <col width={160} />
                      <col width={130} />
                    </colgroup>
                    <Thead position="sticky" top={0} zIndex={1} bg="gray.100">
                      <Tr>
                        <Th {...headerCellStyle}>{t('features.MasterMaintenance.ApprovalRequesterMaster.field.employeeName')}</Th>
                        <Th {...headerCellStyle}>{t('features.MasterMaintenance.ApprovalRequesterMaster.field.employeeOrganization')}</Th>
                        <Th {...headerCellStyle}>{t('features.MasterMaintenance.ApprovalRequesterMaster.field.position')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredEmployees.map(emp => (
                        <Tr
                          key={emp.userId}
                          role="group"
                          cursor="pointer"
                          bg={edit.employeeId === emp.userId ? "blue.100" : "white"}
                          _hover={{ bg: "blue.50" }}
                          onClick={() => handleSelectEmployee(emp)}
                        >
                          <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                            <Text fontWeight={edit.employeeId === emp.userId ? "bold" : "normal"} fontSize="sm">
                              {emp.userName}
                            </Text>
                            <Text fontSize="xs" color="gray.500">{emp.userId}</Text>
                          </Td>
                          <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" fontSize="sm">
                            {emp.organizationName}
                          </Td>
                          <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" fontSize="sm">
                            {emp.positionName}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
                <FormErrorMessage>{errors.employeeId}</FormErrorMessage>
              </FormControl>

              <Divider />

              {/* 有効フラグ */}
              <FormControl h={50}>
                <Flex align="center">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.ApprovalRequesterMaster.field.isActive')}
                  </FormLabel>
                  <Button
                    fontWeight="bold"
                    userSelect="none"
                    cursor="pointer"
                    variant="ghost"
                    _hover={{ bg: 'transparent' }}
                    onClick={() => setEdit(prev => ({ ...prev, isActive: false }))}
                  >
                    {t('features.MasterMaintenance.ApprovalRequesterMaster.label.inactive')}
                  </Button>
                  <Switch
                    isChecked={edit.isActive}
                    onChange={e => setEdit(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  <Button
                    fontWeight="bold"
                    userSelect="none"
                    cursor="pointer"
                    variant="ghost"
                    _hover={{ bg: 'transparent' }}
                    onClick={() => setEdit(prev => ({ ...prev, isActive: true }))}
                  >
                    {t('features.MasterMaintenance.ApprovalRequesterMaster.label.active')}
                  </Button>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={onCloseEdit} m={1}>
              {t('common.button.close')}
            </Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving} m={1}>
              {t('common.button.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 排他確認ダイアログ：同一スロットのアクティブ者を無効化して保存 */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsConfirmOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('features.MasterMaintenance.ApprovalRequesterMaster.modal.conflictTitle')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('features.MasterMaintenance.ApprovalRequesterMaster.modal.conflictBody', { name: conflictPerson })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsConfirmOpen(false)}>
                {t('common.button.cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleConfirmOk} ml={3}>
                {t('features.MasterMaintenance.ApprovalRequesterMaster.modal.conflictConfirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ApprovalRequesterMaster;
